import React from 'react';

export const franklinPierce = {
  presidentNumber: 14,
  president: 'Franklin Pierce',
  subheader: 'Handsome Frank',
  years: '1853-1857',
  predecessor: 'Millard Fillmore',
  successor: 'James Buchanan',
  letter: {
    header: 'Letter to James Buchanan',
    content: `My Dear Buchanan,
I surrender this office with a heavy heart and the bitter knowledge that I have failed our beloved Union. When I entered these chambers four years ago, I believed that adherence to constitutional principle and Democratic Party unity could preserve our federal compact. I leave you a nation more divided than ever, with blood upon the soil of Kansas and hatred poisoning the hearts of North and South alike.

My greatest error was the Kansas-Nebraska Act, which I championed believing it would settle the territorial question through popular sovereignty. Instead, it has unleashed forces of violence and passion that mock our democratic institutions. "Bleeding Kansas" stands as monument to my poor judgment, and I fear the precedent of armed conflict over slavery's expansion may prove irremediable.

I have learned too late that the slavery question cannot be temporized away through political maneuvering. Each attempt at compromise merely intensifies sectional animosity. The Republican Party has risen from the ashes of Whiggery, united solely in opposition to slavery's expansion. They understand what I failed to grasp—this issue admits of no middle ground.

Personal tragedy has shadowed my presidency—the loss of my dear son Benjamin before my inauguration broke something within me that never healed. Perhaps my judgment was clouded by grief, my resolve weakened by sorrow. I pray you possess the strength that has failed me.

The Constitution remains our sacred covenant, but I fear constitutional interpretation alone cannot bridge the moral chasm that divides our people. The very soul of our Republic hangs in the balance. I have done what I believed right, yet I leave you a nation sliding toward the abyss.

May you succeed where I have failed.

Your chastened predecessor, Franklin Pierce`
  },
  biographical: [
    'Born November 23, 1804, in Hillsborough, New Hampshire',
    'Served one term, March 4, 1853 – March 4, 1857',
    'Previously served as U.S. Senator from New Hampshire (1837-1842)',
    'Brigadier General in Mexican-American War',
    'Only president from New Hampshire'
  ],
  presidencyFacts: [
    'Signed Kansas-Nebraska Act, repealing Missouri Compromise',
    'Presided over "Bleeding Kansas" violence',
    'Negotiated Gadsden Purchase from Mexico',
    'Supported Ostend Manifesto for Cuba acquisition',
    'Only president denied renomination by his party'
  ],
  milestones: [
    'Kansas-Nebraska Act passed (1854)',
    'Republican Party formed in opposition (1854)',
    'Gadsden Purchase completed (1854)',
    'Bleeding Kansas violence began (1854)',
    'Denied renomination by Democrats (1856)'
  ],
  personalLife: [
    'Married Jane Appleton in 1834',
    'Had three sons, all died in childhood',
    'Suffered from alcoholism and depression',
    'Lost son Benjamin in train accident before inauguration',
    'Died on October 8, 1869, in Concord, New Hampshire'
  ],
  impactAndLegacy: [
    'Kansas-Nebraska Act accelerated path to Civil War',
    'Helped destroy Whig Party and create Republican Party',
    'Failed to preserve Union through compromise',
    'Ranked among worst presidents by historians',
    'Demonstrated failure of political compromise on slavery'
  ],
  postPresidency: [
    'Retired to New Hampshire',
    'Supported Confederacy during Civil War',
    'Became increasingly isolated and alcoholic',
    'Died on October 8, 1869, at age 64',
    'Buried in Concord, New Hampshire'
  ],
  historicalAnalysis: `Franklin Pierce entered the presidency as a dark horse Democratic candidate who promised to heal national divisions through strict constitutional adherence and party unity. Instead, his administration presided over the collapse of the Second Party System and pushed the nation closer to civil war.

[SECTION] The Kansas-Nebraska Act and Its Consequences

Pierce's presidency was dominated by the Kansas-Nebraska Act of 1854, which he supported despite initial reservations. Proposed by Senator Stephen Douglas, the act organized the Kansas and Nebraska territories under the principle of popular sovereignty, effectively repealing the Missouri Compromise by allowing slavery in territories previously closed to it.

Pierce believed the act would remove slavery from national politics by allowing territorial residents to decide the question locally. Instead, it triggered a massive political upheaval. The act led directly to the formation of the Republican Party, united in opposition to slavery's expansion, and destroyed the Whig Party completely.

More immediately, the act led to violent conflict in Kansas as pro-slavery "Border Ruffians" from Missouri crossed the border to vote illegally, while abolitionists organized the New England Emigrant Aid Company to send anti-slavery settlers. The result was "Bleeding Kansas"—a series of violent confrontations that prefigured the coming Civil War.

[SECTION] Foreign Policy and Expansionism

Pierce pursued an aggressive expansionist foreign policy, seeking to acquire additional territory for the United States. His administration negotiated the Gadsden Purchase from Mexico, acquiring land for a southern transcontinental railroad route.

More controversially, Pierce endorsed the Ostend Manifesto in 1854, a secret diplomatic document that declared the United States would be justified in seizing Cuba from Spain if Spain refused to sell it. When the manifesto became public, Northern opposition was fierce, viewing it as a slaveholder plot to acquire new slave territory.

Pierce also supported filibustering expeditions to Nicaragua led by William Walker, reflecting the administration's sympathy for pro-slavery expansion throughout the Caribbean and Central America.

[SECTION] Domestic Challenges and Personal Struggles

Pierce's presidency was marked by personal tragedy that deeply affected his performance. Just weeks before his inauguration, his eleven-year-old son Benjamin was killed in a train accident before Pierce's eyes. Both Pierce and his wife Jane were devastated, and historians suggest this tragedy contributed to Pierce's increasing reliance on alcohol and his ineffective leadership.

The administration also faced economic challenges, including the Panic of 1857, though this crisis primarily affected Pierce's successor. However, sectional tensions over slavery increasingly dominated all political discourse, making it impossible to address other national issues effectively.

[SECTION] Political Isolation and Party Rejection

By 1856, Pierce had become so unpopular that he became the only elected president denied renomination by his own party. Northern Democrats blamed him for the Kansas crisis and the rise of the Republican Party, while Southern Democrats viewed him as insufficiently committed to their interests.

The Democratic Party instead nominated James Buchanan, who had the advantage of being out of the country as minister to Britain during the Kansas-Nebraska controversy. Pierce's political career effectively ended with his presidency.

[SECTION] Historical Assessment

Historians consistently rank Pierce among the worst American presidents. In recent C-SPAN surveys, he typically ranks in the bottom five, often considered even worse than his successor Buchanan. His presidency is seen as a case study in how well-intentioned policies can have catastrophic unintended consequences.

Pierce's failure lay not in his intentions—he genuinely sought to preserve the Union—but in his fundamental misunderstanding of the slavery issue's moral dimensions. His belief that constitutional procedures and party unity could resolve fundamental moral conflicts proved tragically naive.`,
  sources: [
    '[LINK:https://www.plaidswede.com/product-page/franklin-pierce-new-hampshire-s-favorite-son|Wallner, Peter A. Franklin Pierce: New Hampshire\'s Favorite Son. Plaidswede Publishing, 2004.]',
    '[LINK:https://kansaspress.ku.edu/978-0-7006-0552-6.html|Gara, Larry. The Presidency of Franklin Pierce. University Press of Kansas, 1991.]',
    '[LINK:https://millercenter.org/president/pierce/impact-and-legacy|Miller Center, University of Virginia. "Franklin Pierce: Impact and Legacy."]',
    '[LINK:https://www.upenn.edu/pennpress/book/1390.html|Nichols, Roy Franklin. Franklin Pierce: Young Hickory of the Granite Hills. University of Pennsylvania Press, 1958.]'
  ]
};

export default franklinPierce;